import React, { useState } from "react";
import { Alert, Button, Card, CardBody, Col, FormGroup, Input, Label, Row, Table } from "reactstrap";
import { CajaDiaAnterior, SucursalRendicion, RendicionExistente } from "../types";
import { construirClaveSucursal } from "../helpers";

interface SeleccionStepProps {
  sucursales: SucursalRendicion[];
  sucursalSeleccionada: SucursalRendicion | null;
  cajasDiaAnterior: CajaDiaAnterior[];
  cajaSeleccionadaId: number | null;
  onSucursalChange: (valor: string) => void;
  onCajaSelect: (id: number) => void;
  onReloadCajas: () => void;
  errorSucursales: string | null;
  errorCajas: string | null;
  cargandoSucursales: boolean;
  cargandoCajas: boolean;
  rendicionesExistentes?: Record<number, RendicionExistente>;
  intentosFallidos?: number;
}

const SeleccionStep = ({
  sucursales,
  sucursalSeleccionada,
  cajasDiaAnterior,
  cajaSeleccionadaId,
  onSucursalChange,
  onCajaSelect,
  onReloadCajas,
  errorSucursales,
  errorCajas,
  cargandoSucursales,
  cargandoCajas,
  rendicionesExistentes,
  intentosFallidos = 0,
}: SeleccionStepProps) => {
  const [busqueda, setBusqueda] = useState("");

  const coincideBusqueda = (caja: CajaDiaAnterior) => {
    const termino = busqueda.trim().toLowerCase();
    if (!termino) {
      return true;
    }
    const objetivo = [
      caja.idcontrolcaja,
      caja.turno,
      caja.sucursalNombre,
      caja.sucursal,
      caja.fechaApertura,
      caja.fechaCierre,
      caja.cajero,
      caja.descEstadoCaja,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return objetivo.includes(termino);
  };

  const cajasFiltradas = cajasDiaAnterior.filter(coincideBusqueda);

  return (
  <Row className="g-4">
    <Col>
      <Card>
        <CardBody>
          {intentosFallidos > 0 && (
            <Alert color="warning">
              Se registraron {intentosFallidos} intento{intentosFallidos === 1 ? "" : "s"} fallido
              {intentosFallidos === 1 ? "" : "s"} por superar el tiempo en la vista previa.
            </Alert>
          )}
          {errorSucursales && <Alert color="danger">{errorSucursales}</Alert>}
          <Row className="g-3 mb-3">
            <Col md="6">
              <FormGroup>
                <Label for="sucursalSelect">Sucursal</Label>
                <Input
                  id="sucursalSelect"
                  type="select"
                  value={sucursalSeleccionada ? construirClaveSucursal(sucursalSeleccionada) : ""}
                  onChange={(event) => onSucursalChange(event.target.value)}
                >
                  <option value="">Elegí una sucursal</option>
                  {sucursales.map((sucursal) => (
                    <option key={construirClaveSucursal(sucursal)} value={construirClaveSucursal(sucursal)}>
                      {sucursal.sucursalNombre ?? "Sucursal sin nombre"}
                      {sucursal.empresa?.nombre ? ` · ${sucursal.empresa.nombre}` : ""}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md="6" className="d-flex justify-content-md-end align-items-end">
              <Button color="light" onClick={onReloadCajas} disabled={!sucursalSeleccionada}>
                Actualizar listado
              </Button>
            </Col>
          </Row>
          {cargandoSucursales && <p className="text-muted mb-0">Cargando sucursales asignadas...</p>}
          {!cargandoSucursales && sucursales.length === 0 && (
            <p className="text-muted mb-0">No tenés sucursales asignadas.</p>
          )}
          {errorCajas && <Alert color="danger">{errorCajas}</Alert>}
          {cargandoCajas && <p className="text-muted mb-0">Buscando cajas disponibles...</p>}
          {!cargandoCajas && !sucursalSeleccionada && (
            <p className="text-muted mb-0">Elegí una sucursal para listar las cajas pendientes.</p>
          )}
          {!cargandoCajas && sucursalSeleccionada && cajasDiaAnterior.length === 0 && (
            <p className="text-muted mb-0">No hay cajas pendientes para esta sucursal.</p>
          )}
          {!cargandoCajas && cajasDiaAnterior.length > 0 && (
            <div className="table-responsive">
              <div className="mb-3">
                <Input
                  placeholder="Buscar por caja, turno, sucursal o cajero..."
                  value={busqueda}
                  onChange={(event) => setBusqueda(event.target.value)}
                />
              </div>
              <Table className="align-middle mb-0">
                <thead>
                  <tr>
                    <th></th>
                    <th>Caja</th>
                    <th>Sucursal</th>
                    <th>Estado</th>
                    <th>Declaración de rendición</th>
                    <th>Fecha de caja</th>
                    <th>Cajero</th>
                  </tr>
                </thead>
                <tbody>
                  {cajasFiltradas.map((caja) => {
                    const rendicion = rendicionesExistentes?.[Number(caja.idcontrolcaja)];
                    const estadoRendicion = rendicion?.estado?.toUpperCase() ?? null;
                    const noDisponible = estadoRendicion === "CONFIRMADA";
                    return (
                      <tr key={caja.idcontrolcaja} className={noDisponible ? "opacity-75" : undefined}>
                        <td style={{ width: 50 }}>
                          <Input
                            type="radio"
                            name="caja-select"
                            checked={Number(cajaSeleccionadaId) === Number(caja.idcontrolcaja)}
                          onChange={() => onCajaSelect(Number(caja.idcontrolcaja))}
                          disabled={noDisponible}
                        />
                      </td>
                      <td>
                        <div className="fw-semibold">Caja {caja.idcontrolcaja}</div>
                        <small className="text-muted">Turno {caja.turno ?? "-"}</small>
                        {rendicion && (
                          <small className={noDisponible ? "text-danger d-block" : "text-warning d-block"}>
                            Rendición #{rendicion.rendicionId} en estado {estadoRendicion}
                            {estadoRendicion === "CONFIRMADA" ? (
                              <>
                                {" · Declarada por "}
                                {rendicion.confirmadoPor ||
                                  rendicion.usuarioNombre ||
                                  (rendicion.usuarioId ? `Usuario #${rendicion.usuarioId}` : "Usuario")}
                                {rendicion.confirmadoEn &&
                                  ` el ${new Date(rendicion.confirmadoEn).toLocaleString("es-PY")}`}
                              </>
                            ) : (
                              <>
                                {" · Guardado por "}
                                {rendicion.usuarioNombre || (rendicion.usuarioId ? `Usuario #${rendicion.usuarioId}` : "Usuario")}
                                {rendicion.actualizadoEn &&
                                  ` el ${new Date(rendicion.actualizadoEn).toLocaleString("es-PY")}`}
                              </>
                            )}
                          </small>
                        )}
                      </td>
                      <td>
                        <div className="fw-semibold">{caja.sucursalNombre ?? caja.sucursal ?? "-"}</div>
                        <small className="text-muted">ID {caja.idsucursal ?? "?"}</small>
                      </td>
                      <td>
                        <span className="badge bg-info-subtle text-info">
                          {(() => {
                            const estadoBase = caja.descEstadoCaja ?? caja.estado ?? "Sin estado";
                            if ((estadoBase ?? "").toUpperCase() === "CERRADO") {
                              return "CERRADO EN JEDI";
                            }
                            return estadoBase || "Sin estado";
                          })()}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`badge ${estadoRendicion === "CONFIRMADA" ? "bg-success-subtle text-success" : "bg-warning-subtle text-warning"}`}
                        >
                          {estadoRendicion === "CONFIRMADA" ? "Completada" : "Pendiente"}
                        </span>
                      </td>
                      <td>
                        <div>{caja.fechaApertura ? new Date(caja.fechaApertura).toLocaleString("es-PY") : "-"}</div>
                        <small className="text-muted">
                          {caja.fechaCierre ? `Cierre: ${new Date(caja.fechaCierre).toLocaleString("es-PY")}` : ""}
                        </small>
                      </td>
                      <td>{caja.cajero ?? "No informado"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>
    </Col>
  </Row>
  );
};

export default SeleccionStep;
